import { useState, useRef, useEffect } from 'react';
import {
  X,
  Video,
  ImageIcon,
  Smartphone,
  Sun,
  Clock,
  Circle,
  Square,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { useWebVideoRecorder, formatDuration, getMaxDurationSeconds } from '@/hooks/useWebVideoRecorder';
import { uploadVideo, type VideoMetadata } from '@/services/videoService';
import styles from './VideoRecordModal.module.css';

interface VideoRecordModalProps {
  visible: boolean;
  roomName: string;
  onClose: () => void;
  onComplete: () => void;
  cleaningId?: string;
  roomId?: string;
}

type ModalState = 'options' | 'preview' | 'recording' | 'uploading';

export function VideoRecordModal({
  visible,
  roomName,
  onClose,
  onComplete,
  cleaningId,
  roomId,
}: VideoRecordModalProps) {
  const [modalState, setModalState] = useState<ModalState>('options');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const {
    isRecording,
    isPreviewing,
    isSupported,
    duration,
    showWarning,
    error: recorderError,
    startPreview,
    startRecording,
    stopRecording,
    stopPreview,
    clearError,
  } = useWebVideoRecorder();

  useEffect(() => {
    if (!visible) {
      setModalState('options');
      setError(null);
      setUploadProgress(0);
      stopPreview();
      clearError();
    }
  }, [visible, stopPreview, clearError]);

  useEffect(() => {
    if (recorderError) setError(recorderError);
  }, [recorderError]);

  const handleClose = () => {
    stopPreview();
    onClose();
  };

  const handleStartCamera = async () => {
    if (!isSupported) {
      setError('Video recording is not supported in this browser');
      return;
    }
    setError(null);
    setModalState('preview');
    setTimeout(async () => {
      if (videoRef.current) await startPreview(videoRef.current);
    }, 100);
  };

  const handleStartRecording = () => {
    setError(null);
    startRecording();
    setModalState('recording');
  };

  const handleStopRecording = async () => {
    const blob = await stopRecording();
    if (!blob) {
      setError('Failed to capture video');
      setModalState('options');
      return;
    }

    if (cleaningId && roomId) {
      setModalState('uploading');
      const metadata: VideoMetadata = { cleaningId, roomId, durationSeconds: duration };
      const result = await uploadVideo(metadata, blob, (progress) => {
        setUploadProgress(progress.percent);
      });
      if (result.error) {
        setError(result.error.message);
        setModalState('options');
        return;
      }
    }

    stopPreview();
    onComplete();
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      console.log('[VideoImport] File selected:', {
        name: file.name,
        type: file.type,
        size: file.size,
      });

      if (file.size > 100 * 1024 * 1024) {
        setError('Video file is too large. Maximum size is 100MB.');
        return;
      }

      if (cleaningId && roomId) {
        setModalState('uploading');
        const metadata: VideoMetadata = { cleaningId, roomId, durationSeconds: 0 };
        const result = await uploadVideo(metadata, file, (progress) => {
          setUploadProgress(progress.percent);
        });
        if (result.error) {
          setError(result.error.message);
          setModalState('options');
          return;
        }
      }
      onComplete();
    };
    input.click();
  };

  if (!visible) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.backdrop} onClick={handleClose} />
      <div className={`${styles.container} ${(modalState === 'preview' || modalState === 'recording') ? styles.containerFull : ''}`}>
        {modalState === 'options' && <div className={styles.handle} />}

        <div className={styles.header}>
          <h2 className={styles.title}>
            {modalState === 'recording' ? 'Recording' : 'Record Video'}
          </h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <p className={styles.subtitle}>{roomName}</p>

        {modalState === 'uploading' && (
          <div className={styles.uploadingContainer}>
            <Loader2 size={32} className={styles.spinner} />
            <p className={styles.uploadingText}>Uploading video...</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
            </div>
            <span className={styles.progressText}>{Math.round(uploadProgress)}%</span>
          </div>
        )}

        {(modalState === 'preview' || modalState === 'recording') && (
          <div className={styles.cameraContainer}>
            <video ref={videoRef} className={styles.videoPreview} playsInline muted />

            {isRecording && (
              <div className={styles.recordingOverlay}>
                <div className={styles.recordingIndicator}>
                  <Circle size={12} fill="var(--color-error)" />
                  <span className={styles.recordingTime}>{formatDuration(duration)}</span>
                  <span className={styles.recordingMax}>/ {formatDuration(getMaxDurationSeconds())}</span>
                </div>
                {showWarning && (
                  <div className={styles.warningBadge}>
                    <AlertTriangle size={14} />
                    <span>30 seconds remaining</span>
                  </div>
                )}
              </div>
            )}

            <div className={styles.cameraControls}>
              {isRecording ? (
                <button className={styles.stopButton} onClick={handleStopRecording}>
                  <Square size={24} fill="white" />
                </button>
              ) : isPreviewing ? (
                <button className={styles.recordButton} onClick={handleStartRecording}>
                  <Circle size={32} fill="var(--color-error)" />
                </button>
              ) : (
                <Loader2 size={32} className={styles.spinner} />
              )}
            </div>

            {!isRecording && (
              <button
                className={styles.backButton}
                onClick={() => { stopPreview(); setModalState('options'); }}
              >
                Cancel
              </button>
            )}
          </div>
        )}

        {modalState === 'options' && (
          <>
            <div className={styles.tipsContainer}>
              <p className={styles.tipsTitle}>Tips for a great video</p>
              <div className={styles.tipsList}>
                <div className={styles.tipItem}>
                  <div className={styles.tipIcon}><Smartphone size={16} /></div>
                  <span>Hold your phone upright (portrait)</span>
                </div>
                <div className={styles.tipItem}>
                  <div className={styles.tipIcon}><Video size={16} /></div>
                  <span>Pan slowly around the room</span>
                </div>
                <div className={styles.tipItem}>
                  <div className={styles.tipIcon}><Sun size={16} /></div>
                  <span>Ensure good lighting</span>
                </div>
                <div className={styles.tipItem}>
                  <div className={styles.tipIcon}><Clock size={16} /></div>
                  <span>Maximum 3 minutes per video</span>
                </div>
              </div>
            </div>

            {error && (
              <div className={styles.errorContainer}>
                <p>{error}</p>
              </div>
            )}

            <div className={styles.buttonsContainer}>
              <button
                className={`${styles.primaryButton} ${!isSupported ? styles.buttonDisabled : ''}`}
                onClick={handleStartCamera}
                disabled={!isSupported}
              >
                <Video size={20} />
                <span>{isSupported ? 'Record Video' : 'Camera Not Available'}</span>
              </button>

              <button className={styles.secondaryButton} onClick={handleImport}>
                <ImageIcon size={20} />
                <span>Import from Gallery</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


